/**
 * Helpers de Web Push que no dependen de React (los usa el hook y el registro del SW).
 */

export const PUSH_FEATURE_FLAG = 'web_push_notifications';

/**
 * Marcador en la URL del script del SW de push. Registrar con otro scriptURL (mismo scope)
 * reemplaza al worker viejo de next-pwa (`/sw.js` sin query), y permite distinguir ambos.
 * Subirlo (push-2…) fuerza un reemplazo; `next.config` sirve /sw.js sin caché con o sin query.
 */
export const PUSH_SW_MARKER = 'v=push-1';

/** Ruta del service worker, respetando basePath si la app se sirve bajo un prefijo. */
export const getServiceWorkerUrl = () =>
  `${process.env.NEXT_PUBLIC_BASE_PATH || ''}/sw.js?${PUSH_SW_MARKER}`;

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS se presenta como Mac pero con touch.
    (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1));

export type PushBrowser =
  'chrome' | 'edge' | 'firefox' | 'safari' | 'samsung' | 'other';
export type PushOS =
  'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'other';

/**
 * Navegador y SO por user agent. Los WebView in-app (Facebook, Instagram, WhatsApp, `; wv)`,
 * iOS sin token Safari) se reportan como 'other': ahí no se puede recibir push.
 */
export const detectPlatform = (): { browser: PushBrowser; os: PushOS } => {
  if (typeof navigator === 'undefined')
    return { browser: 'other', os: 'other' };
  const ua = navigator.userAgent;
  let os: PushOS = 'other';
  if (isIOS()) os = 'ios';
  else if (/Android/i.test(ua)) os = 'android';
  else if (/Windows/i.test(ua)) os = 'windows';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macos';
  else if (/Linux|X11|CrOS/i.test(ua)) os = 'linux';

  const inApp =
    /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|Snapchat|WhatsApp|Twitter|LinkedInApp|; wv\)/i.test(
      ua
    ) ||
    (os === 'ios' && !/Safari\//.test(ua));
  if (inApp) return { browser: 'other', os };

  let browser: PushBrowser = 'other';
  if (/SamsungBrowser/i.test(ua)) browser = 'samsung';
  else if (/Edg(e|A|iOS)?\//.test(ua)) browser = 'edge';
  else if (/Firefox|FxiOS/i.test(ua)) browser = 'firefox';
  else if (/Chrome|CriOS|Chromium/i.test(ua)) browser = 'chrome';
  else if (/Safari/i.test(ua)) browser = 'safari';
  return { browser, os };
};

/** True si la app corre instalada (agregada a la pantalla de inicio / instalada como PWA). */
export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    nav.standalone === true
  );
};

export const getNotificationPermission = (): NotificationPermission =>
  typeof Notification === 'undefined' ? 'denied' : Notification.permission;

/** La clave pública VAPID (base64url) en el formato que pide `pushManager.subscribe`. */
export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
};

export const getVapidPublicKey = () =>
  process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || '';

/**
 * Registra (o devuelve) el service worker de push. `updateViaCache: 'none'` fuerza a
 * revalidar sw.js en cada carga: así el worker viejo de next-pwa (misma URL) se reemplaza.
 */
export const registerPushServiceWorker =
  async (): Promise<ServiceWorkerRegistration | null> => {
    if (!isPushSupported()) return null;
    try {
      return await navigator.serviceWorker.register(getServiceWorkerUrl(), {
        scope: `${process.env.NEXT_PUBLIC_BASE_PATH || ''}/`,
        updateViaCache: 'none',
      });
    } catch {
      return null;
    }
  };

export const getPushSubscription = async () => {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration(
    `${process.env.NEXT_PUBLIC_BASE_PATH || ''}/`
  );
  if (!registration) return null;
  return registration.pushManager.getSubscription();
};

/** "Ahora no" del banner: se pospone 14 días, por usuario (no por navegador). */
export const PUSH_BANNER_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

export const pushBannerSnoozeKey = (userId: string | number | undefined) =>
  `push_banner_snoozed_at:${userId ?? 'anon'}`;

/** True si `snoozedAt` (ms epoch, como string o número) está dentro de la ventana de 14 días. */
export const isPushBannerSnoozed = (
  snoozedAt: string | number | null | undefined,
  now: number = Date.now()
): boolean => {
  const at = Number(snoozedAt);
  if (!snoozedAt || !Number.isFinite(at) || at <= 0) return false;
  // `at` en el futuro (reloj movido) no debe silenciar el banner para siempre.
  if (at > now) return false;
  return now - at < PUSH_BANNER_SNOOZE_MS;
};

export const getPushBannerSnoozedAt = (userId?: string | number) => {
  try {
    return window.localStorage.getItem(pushBannerSnoozeKey(userId));
  } catch {
    return null;
  }
};

export const snoozePushBanner = (userId?: string | number) => {
  try {
    window.localStorage.setItem(
      pushBannerSnoozeKey(userId),
      String(Date.now())
    );
  } catch {
    // Sin storage: el "Ahora no" vale solo mientras el banner siga montado.
  }
};

/** Notifica a todas las instancias de `usePushNotifications` que cambió el estado del dispositivo. */
export const PUSH_DEVICE_CHANGED_EVENT = 'push-device-changed';
